import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { LOGO_LIGHT } from "@/lib/brand";

const LOGO_URL = LOGO_LIGHT;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Joseph.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-cream" data-testid="login-page">
      <Toaster richColors position="top-right" />
      {/* Left brand panel */}
      <div className="hidden lg:flex relative bg-navy text-cream flex-col justify-between p-12 overflow-hidden">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Joseph Poche" decoding="async" className="h-20 w-20 object-contain" />
          <div className="leading-tight">
            <div className="font-serif text-cream text-lg">Joseph Poche</div>
            <div className="overline text-gold">Investor · Builder · Operator</div>
          </div>
        </div>
        <div>
          <div className="gold-rule mb-6" />
          <div className="editorial-h2 text-4xl text-cream">
            Private workspace.<br />
            <span className="italic text-gold">Pipeline, contacts, capital.</span>
          </div>
          <p className="mt-6 text-cream/70 text-sm max-w-md font-light">
            Sign in to manage your network, deal pipeline, follow-ups and AI-drafted communications.
          </p>
        </div>
        <div className="text-xs text-cream/40">© {new Date().getFullYear()} Joseph Poche</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="back-to-site" className="overline text-gold-deep hover:text-navy">
            ← Back to site
          </Link>
          <h1 className="editorial-h1 text-4xl text-navy mt-6">Sign in</h1>
          <div className="gold-rule mt-4 mb-8" />

          <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="overline text-navy/60">Email</label>
              <input
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-3 focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline text-navy/60">Password</label>
              <input
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-3 focus:outline-none focus:border-gold"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-destructive border-l-2 border-destructive pl-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-navy text-gold py-3 text-xs uppercase tracking-widest hover:bg-navy-600 transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Enter CRM"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
